/**
 * DXF LTYPE table → Three.js Line2 dash configuration.
 *
 * # Why this module exists (Q26)
 *
 * `linetypeToSvgDashArray` (apps/web/src/app/linetypes/standardLinetypes.ts)
 * encodes patterns as plain dash/gap lengths. That is great for
 * `<svg stroke-dasharray>` but Line2's `LineMaterial` exposes
 * `dashSize`, `gapSize`, `dashOffset` only — single dash/gap pair plus
 * an offset. Multi-segment patterns (CENTER, DASHDOT, etc.) require us
 * to *summarise* the AutoCAD pattern into the closest single dash/gap
 * approximation.
 *
 * The summary picks the longest dash and the longest gap from the
 * pattern array. AutoCAD parity is good for line types that are
 * dominated by one dash-gap motif (DASHED, DOT, HIDDEN). For long-form
 * patterns (CENTER, PHANTOM) the approximation is visually close
 * enough at the camera scales we render at and matches the legacy SVG
 * fall-back via `linetypeToSvgDashArray` exactly when the pattern is a
 * 2-element array.
 *
 * Q26.C — `computeEffectiveDash` factors LTSCALE / MSLTSCALE /
 * PSLTSCALE into the `dashSize` / `gapSize` returned to Line2 on every
 * camera change. Renderer side calls this on each frame the camera
 * pose changed.
 */

import {
  STANDARD_LINETYPES,
  findStandardLinetype,
  type StandardLinetype,
} from "../../linetypes/standardLinetypes";

/** Dash configuration consumed by Three.js `LineMaterial`. */
export interface DashConfig {
  /** Length of the dash segment in world units (post-scale). */
  dashSize: number;
  /** Length of the gap segment in world units (post-scale). */
  gapSize: number;
  /** Offset along the line for animated patterns; always 0 for static. */
  dashOffset: number;
}

/**
 * PR-LT3 variant A (A1) — FULL multi-element linetype pattern.
 *
 * # Why this exists alongside `DashConfig`
 *
 * `DashConfig` carries the legacy single (dash, gap) pair the original
 * `LineMaterial` shader consumed — adequate for DASHED / HIDDEN (a plain
 * 2-element motif) but LOSSY for CENTER / DASHDOT / DOT / PHANTOM, which the
 * `summarise*` helpers collapse to one dash/gap. `DashPattern` preserves the
 * SIGNED element array unchanged so the multi-element shader texture path can
 * walk the real long-dash·gap·dot·gap… sequence, matching AutoCAD + the PDF
 * exporter (`print/pdf/linetypePatternsFull.ts`, the same signed `number[]`).
 *
 * `elements` uses DXF group-49 sign semantics (drawing units):
 *   - `> 0` = dash (pen-down) of that length,
 *   - `< 0` = gap  (pen-up)  of `|length|`,
 *   - `== 0` = dot  (zero-length pen-down round mark).
 *
 * `totalLength` is the sum of `|element|` over all elements — the pattern's
 * repeat period in drawing units. A dot (`0`) contributes 0 to the period; the
 * shader renders it as a tiny round mark at its position WITHOUT consuming
 * period length (parity with AutoCAD, where a dot is a zero-length segment).
 */
export interface DashPattern {
  /** Signed element lengths (drawing units): >0 dash, <0 gap, 0 dot. */
  elements: number[];
  /** Repeat period = Σ|element| (drawing units). Dots contribute 0. */
  totalLength: number;
}

/**
 * A1 — dot sentinel threshold (mm-equivalent). The web `dashPattern` arrays in
 * `standardLinetypes.ts` encode a dot as the `0.01` sentinel; the SVG renderer
 * treats any length `< 0.1` as a dot. Mirrors `print/pdf/linetypePatternsFull.ts`
 * exactly so a dot stays a dot across screen + PDF.
 */
const DOT_THRESHOLD = 0.1;

/**
 * A1 — whether a pattern needs the FULL multi-element texture path (vs the
 * single-pair `DashConfig` fast path). True when the pattern has > 2 elements
 * (CENTER / DASHDOT / PHANTOM …, which the single-pair summary collapses) OR
 * contains a DOT (a sub-{@link DOT_THRESHOLD} length, which `summarise*`
 * promotes to a tiny dash — losing the dot). A plain 2-element dash/gap
 * (DASHED / HIDDEN / ISO02) is summarised faithfully and keeps the fast path.
 *
 * Mirrors `linetypePatternsFull.needsFullPattern` so screen + PDF agree on
 * which linetypes get the full treatment.
 */
export function needsFullPattern(pattern: readonly number[]): boolean {
  if (pattern.length > 2) return true;
  // A DOT makes a 2-element pattern lossy (the single-pair summary promotes it
  // to a 0.1 dash, losing the dot). A dot appears as either the web `0.01`
  // sentinel (`|v| < DOT_THRESHOLD`, used by the standard registry) OR an
  // already-signed exact `0` (used by a custom drawing-units LTYPE) — both are
  // covered by `|v| < DOT_THRESHOLD` (abs(0) = 0). They must route to the full
  // pattern so the dot renders as a point, not a gap.
  return pattern.some((v) => Math.abs(v) < DOT_THRESHOLD);
}

/**
 * A1 — build a {@link DashPattern} from a SIGNED group-49 sequence (drawing
 * units; >0 dash, <0 gap, 0 dot). Preserves the array verbatim (NO collapse)
 * and computes `totalLength = Σ|element|`. Returns `null` for an empty array or
 * a degenerate all-gap pattern (nothing to draw → caller falls back to solid /
 * the single-pair path).
 */
export function signedToDashPattern(signed: readonly number[]): DashPattern | null {
  if (signed.length === 0) return null;
  let total = 0;
  let hasDrawn = false;
  for (const v of signed) {
    total += Math.abs(v);
    if (v >= 0) hasDrawn = true; // a dash (>0) or dot (==0) is a pen-down mark
  }
  if (total <= 0 || !hasDrawn) return null;
  return { elements: [...signed], totalLength: total };
}

/**
 * A1 — convert a web `dashPattern` array (all-positive mm magnitudes, dots
 * encoded as the `0.01` sentinel at EVEN/dash positions) into the DXF-signed
 * element array (>0 dash, <0 gap, 0 dot). Mirrors
 * `linetypePatternsFull.toSignedElements` so the screen registry matches the
 * PDF exporter byte-for-byte in sign + magnitude.
 *
 * Even indices are dash positions (sub-threshold → dot `0`); odd indices are
 * gap positions (→ negative magnitude).
 */
export function webDashPatternToSigned(dashPattern: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < dashPattern.length; i++) {
    const v = dashPattern[i] ?? 0;
    if (i % 2 === 0) {
      out.push(v < DOT_THRESHOLD ? 0 : v);
    } else {
      out.push(-Math.abs(v));
    }
  }
  return out;
}

/**
 * A1 — registry: case-folded STANDARD linetype name → full SIGNED element
 * array (drawing units, mm-calibrated to match the on-screen single-pair
 * registry). Built once at module load from {@link STANDARD_LINETYPES}; only
 * names whose summary is LOSSY ({@link needsFullPattern}) are registered — a
 * faithful 2-element dash/gap (DASHED / HIDDEN) is absent here and keeps the
 * single-pair fast path. Parallels `linetypePatternsFull.FULL_PATTERN_REGISTRY`
 * but in mm units (web registry) rather than the PDF's identical mm source.
 */
const FULL_PATTERN_REGISTRY: ReadonlyMap<string, readonly number[]> = (() => {
  const map = new Map<string, readonly number[]>();
  for (const lt of STANDARD_LINETYPES) {
    const key = lt.name.trim().toLowerCase();
    if (key === "continuous" || key === "bylayer" || key === "byblock") continue;
    if (lt.dashPattern.length === 0) continue;
    if (!needsFullPattern(lt.dashPattern)) continue;
    map.set(key, Object.freeze(webDashPatternToSigned(lt.dashPattern)));
  }
  return map;
})();

/**
 * A1 — look up the FULL {@link DashPattern} for a STANDARD linetype by
 * case-insensitive name, in the mm-calibrated on-screen units. Returns `null`
 * for a sentinel / unknown name, OR a standard name the single-pair summary
 * already renders faithfully (a plain 2-element dash/gap) — in every `null`
 * case the caller keeps the single-pair `DashConfig` fast path.
 */
export function standardLinetypeFullPattern(
  name: string | undefined,
): DashPattern | null {
  if (!name) return null;
  const key = name.trim().toLowerCase();
  const signed = FULL_PATTERN_REGISTRY.get(key);
  return signed ? signedToDashPattern(signed) : null;
}

/** Plain (un-scaled) dash config — `linetypeToDashConfig` returns this. */
export const CONTINUOUS_DASH: DashConfig = {
  dashSize: 0,
  gapSize: 0,
  dashOffset: 0,
};

/**
 * Reduce a multi-element AutoCAD `dashPattern` array to a single
 * (dashSize, gapSize) pair. Strategy:
 *   - Even indices = dash lengths, odd indices = gap lengths.
 *   - A length below `0.1` (the dot threshold used by the SVG path) is
 *     promoted to the smallest non-dot dash so Line2 still draws a
 *     visible mark instead of collapsing to zero.
 *   - Final dash = max of all dash lengths; final gap = max of all gap
 *     lengths. Picking the *max* keeps dotted-dash patterns from
 *     visually disappearing when the gap dominates.
 */
function summariseDashPattern(pattern: number[]): {
  dashSize: number;
  gapSize: number;
} {
  if (pattern.length === 0) return { dashSize: 0, gapSize: 0 };
  let maxDash = 0;
  let maxGap = 0;
  for (let i = 0; i < pattern.length; i++) {
    const v = Math.max(pattern[i] ?? 0, 0.1);
    if (i % 2 === 0) {
      if (v > maxDash) maxDash = v;
    } else {
      if (v > maxGap) maxGap = v;
    }
  }
  // Even-length pattern with no gap component → single dash run.
  if (maxGap === 0) maxGap = maxDash * 0.5;
  return { dashSize: maxDash, gapSize: maxGap };
}

/**
 * Public registry: `name → DashConfig`. Built at module load from the
 * STANDARD_LINETYPES array so we stay in lockstep with the SVG renderer.
 *
 * Names are stored lower-cased; the lookup helper normalises on read.
 *
 * TODO(F14): this still iterates the WEB `standardLinetypes.ts` array
 * (`dashPattern` magnitudes, mm units, mixed-case names). PR-LT3's primary
 * render path (`linetypeResolve.resolveBucketLinetypeDash`) now sources its
 * patterns from the `drawing-model` CORE seed (`STANDARD_LINETYPES` record,
 * inch units, `elements[]`). This registry is retained ONLY as the mm-unit
 * fallback for the web names the inch-unit core seed does not key (and for the
 * SVG / PDF dropdown consumers in `standardLinetypes.ts` / PropertyGrid /
 * `linetypePatternsFull.ts`). Full dedup of the web hand-copy is deferred
 * because the two tables differ in unit (mm vs inch), name casing, and the
 * hand-tuned `…2` half-variant magnitudes — converting in place would change
 * the SVG dash output + ~15 `standardLinetypes.test.ts` assertions. Tracked as
 * F14 in the object-graph plan.
 */
export const LINETYPE_DASH_REGISTRY: ReadonlyMap<string, DashConfig> =
  (() => {
    const map = new Map<string, DashConfig>();
    for (const lt of STANDARD_LINETYPES) {
      map.set(lt.name.toLowerCase(), {
        ...summariseDashPattern(lt.dashPattern),
        dashOffset: 0,
      });
    }
    return map;
  })();

/**
 * Look up the dash configuration for a linetype by case-insensitive
 * name. Returns `CONTINUOUS_DASH` when the name is unknown / sentinel
 * (`Continuous`, `ByLayer`, `ByBlock`).
 */
export function linetypeToDashConfig(name: string | undefined): DashConfig {
  if (!name) return CONTINUOUS_DASH;
  const key = name.trim().toLowerCase();
  if (!key || key === "continuous" || key === "bylayer" || key === "byblock") {
    return CONTINUOUS_DASH;
  }
  const config = LINETYPE_DASH_REGISTRY.get(key);
  return config ?? CONTINUOUS_DASH;
}

/**
 * Reduce a SIGNED group-49 sequence to a single (dashSize, gapSize)
 * pair by classifying each element by its SIGN — NOT by its array
 * index parity. DXF group-49 semantics: `> 0` = dash, `< 0` = gap,
 * `== 0` = dot.
 *
 * # Why sign, not index parity
 *
 * The legacy `summariseDashPattern` assumed even index ⇒ dash, odd
 * index ⇒ gap. That holds for the WEB `dashPattern` arrays (already
 * split into alternating dash/gap magnitudes), but NOT for a raw
 * signed group-49 sequence whose signs need not alternate from index
 * 0 — especially complex text/shape host segments, which
 * `signedFromElement` preserves with their true sign. A negative gap
 * landing at an even index was being counted as a dash (and a positive
 * dash at an odd index as a gap), swapping the dash/gap pair. Classify
 * by sign so the bucket is always correct regardless of position.
 *
 * Strategy mirrors `summariseDashPattern`: take the MAX of each
 * sign-classified bucket (max keeps the visible mark from collapsing
 * when one motif dominates); a dot (`0`) contributes a `0.1` minimum
 * dash so Line2 still draws a visible mark instead of nothing.
 */
function summariseSignedPattern(signed: number[]): {
  dashSize: number;
  gapSize: number;
} {
  if (signed.length === 0) return { dashSize: 0, gapSize: 0 };
  let maxDash = 0;
  let maxGap = 0;
  for (const v of signed) {
    if (v > 0) {
      // Dash run; clamp tiny dashes up to the dot threshold so the
      // mark stays visible (matches `summariseDashPattern`).
      const d = Math.max(v, 0.1);
      if (d > maxDash) maxDash = d;
    } else if (v < 0) {
      const g = Math.max(-v, 0.1);
      if (g > maxGap) maxGap = g;
    } else {
      // Dot (== 0): a zero-length dash. Promote to the dot threshold
      // so Line2 paints a round-cap dot rather than collapsing to a
      // continuous line.
      if (0.1 > maxDash) maxDash = 0.1;
    }
  }
  // All-dash (no gap component) → single dash run with a synthetic gap
  // so the material still renders as dashed (parity with
  // `summariseDashPattern`).
  if (maxGap === 0 && maxDash > 0) maxGap = maxDash * 0.5;
  return { dashSize: maxDash, gapSize: maxGap };
}

/**
 * Compile a custom user-defined LTYPE entry (DXF group code 49 = dash
 * length, 74 = complex shape — we currently ignore the shape codes;
 * future PR adds shape-line support).
 *
 * @param dashLengths Array of group-49 values from the DXF LTYPE entry.
 *                    Positive = dash, negative = gap, zero = dot.
 *                    Matches DXF semantics directly. Classification is
 *                    by SIGN, not array-index parity (the signs of a
 *                    raw group-49 sequence need not alternate from
 *                    index 0 — e.g. a leading gap, or a complex
 *                    text/shape host segment).
 */
export function compileCustomLtype(dashLengths: number[]): DashConfig {
  if (dashLengths.length === 0) return CONTINUOUS_DASH;
  const { dashSize, gapSize } = summariseSignedPattern(dashLengths);
  return { dashSize, gapSize, dashOffset: 0 };
}

/** Viewport kind matters for LTSCALE/MSLTSCALE/PSLTSCALE selection. */
export type LinetypeViewportKind = "model" | "paper" | "viewport";

/** Subset of editor sysvars consumed by `computeEffectiveDash`. */
export interface LinetypeSysvars {
  /** Global linetype scale, default 1. */
  LTSCALE: number;
  /** Model-space linetype scale, default 1. */
  MSLTSCALE: number;
  /** Paper-space linetype scale, default 1. */
  PSLTSCALE: number;
  /** Per-entity linetype scale (CELTSCALE), default 1. */
  CELTSCALE?: number;
}

/**
 * Q26.C — apply LTSCALE / MSLTSCALE / PSLTSCALE / CELTSCALE / lineweight
 * to the base dash config. Renderer calls this on every camera change.
 *
 * Rules (AutoCAD reference):
 *   - Model space: effective scale = LTSCALE × CELTSCALE (× MSLTSCALE
 *     when MSLTSCALE=1, applied per the active annotation scale —
 *     simplified here; full annotative scaling lives in PR-I).
 *   - Paper space: effective scale = LTSCALE × CELTSCALE (× PSLTSCALE
 *     when PSLTSCALE=1; controls whether viewport linetype scaling
 *     follows the viewport zoom).
 *   - Lineweight (mm) does NOT change dash length in AutoCAD — it only
 *     affects stroke thickness — so we ignore it here. The argument is
 *     accepted for API symmetry with the spec (Q19.C uses it on the
 *     line-width side).
 */
export function computeEffectiveDash(
  base: DashConfig,
  _lineweightMm: number,
  viewportKind: LinetypeViewportKind,
  sysvars: LinetypeSysvars,
): DashConfig {
  if (base.dashSize === 0 && base.gapSize === 0) return CONTINUOUS_DASH;

  const ltscale = sysvars.LTSCALE > 0 ? sysvars.LTSCALE : 1;
  const celtscale = sysvars.CELTSCALE !== undefined && sysvars.CELTSCALE > 0
    ? sysvars.CELTSCALE
    : 1;

  let scale = ltscale * celtscale;
  if (viewportKind === "model" && sysvars.MSLTSCALE > 0) {
    scale *= sysvars.MSLTSCALE;
  } else if (viewportKind === "paper") {
    // PSLTSCALE acts as a flag in AutoCAD — when 1, paper-space
    // honours LTSCALE; when 0, dash pattern ignores LTSCALE (CELTSCALE
    // alone applies). Documented in AutoCAD's PSLTSCALE help page.
    if (sysvars.PSLTSCALE === 0) {
      scale = celtscale;
    }
  }

  return {
    dashSize: base.dashSize * scale,
    gapSize: base.gapSize * scale,
    dashOffset: base.dashOffset,
  };
}

/**
 * PR-LT3 variant A (A2) — recover the GLOBAL linetype-scale scalar
 * (LTSCALE × MSLTSCALE | PSLTSCALE-flag), **excluding** the per-entity
 * CELTSCALE term.
 *
 * # Why the split (A2 contract)
 *
 * AutoCAD's effective dash scale is
 * `LTSCALE × (MSLTSCALE | PSLTSCALE-flag) × CELTSCALE`. The scene renderer
 * applies the two factors in different places:
 *   - The **global** factor (this function) is uniform across a whole pass,
 *     so it rides the existing `dashScale` material uniform — one value per
 *     bucket material, selected by the bucket's model/paper space.
 *   - The **per-entity** CELTSCALE varies entity-to-entity inside one
 *     bucket (CELTSCALE is NOT part of the MeshKey — adding it would explode
 *     buckets), so it travels as a per-instance vertex attribute
 *     (`instanceLtScale`) and is multiplied into `vLineDistance` in the
 *     vertex shader on top of `dashScale`.
 *
 * The scalar is recovered the same way the PDF exporter does it
 * (`print/pdf/batchPaint.ts`): run `computeEffectiveDash` on a UNIT base
 * `{dashSize:1}` with `CELTSCALE` forced to 1, so the returned `dashSize`
 * IS the global factor — including the `PSLTSCALE === 0` edge case — with no
 * re-derivation of the rule. Returns `1` for a degenerate / non-positive
 * result so a missing sysvar table preserves the pre-A2 behaviour (scale 1).
 */
export function computeGlobalLtScale(
  sysvars: LinetypeSysvars,
  viewportKind: LinetypeViewportKind,
): number {
  const unit = computeEffectiveDash(
    { dashSize: 1, gapSize: 0, dashOffset: 0 },
    0,
    viewportKind,
    // Force CELTSCALE out of the GLOBAL factor — it is applied per-instance.
    { ...sysvars, CELTSCALE: 1 },
  );
  return unit.dashSize > 0 ? unit.dashSize : 1;
}

/** Re-export the source-of-truth registry for tests. */
export { STANDARD_LINETYPES, findStandardLinetype, type StandardLinetype };
